import React, {useEffect, useState} from 'react';
import {AppInterface, InvalidPanel, PageLinkSharePanel, RootInternalDialog, ThemedDrawer, ThemedIconButton} from "../../index";
import {checkNullStr, getDefStr, isBoolTrue, setTabTitle} from "../../../common/fusion-ui/utils/ReactUtils";
import {Anchor, Flexbox, GridBox, Section} from "../../../common/fusion-ui";
import {useTheme} from "../../../common/ProjectThemeProvider";
import {Keys, ModuleRouteUrls, ShowViewType} from "../../config/AppConfig";
import {useConfig} from "../../context/ConfigProvider";
import {isCurrUserRoleAdmin} from "../../utils/AppUtils";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";

const FormPageRoot = React.memo(({
                                     tag,
                                     moduleName,
                                     updateOnly,
                                     isDataLoading,
                                     isDataFound,
                                     dataLoadingMsg,
                                     editPageTitle,
                                     addPageTitle,
                                     showBack = true,
                                     backUrl,
                                     showShare = false,
                                     shareUrl,
                                     md = 7,
                                     drawerWidth = '60%',

                                     open,
                                     onHide,
                                     showAs = ShowViewType.PAGE,

                                     children,
                                     freeComponent
                                 }) => {
    const TAG = "FormPageRoot";

    const theme = useTheme();
    const {updateConfig, getConfig, isConfigTrue} = useConfig();
    const [showShareDialog, setShowShareDialog] = useState(false);
    const {isScreenUpSm, isScreenXs, isScreenSm} = useScreenSize();

    useEffect(() => {
        if (showAs == ShowViewType.PAGE) {
            setTabTitle(getTabTitle());
            updateConfig(Keys.config_showHomeRightPanels, false);
            updateConfig(Keys.config_ShowTabBar, false);
            updateConfig(Keys.config_TabBarTab, -1);
        }
        updateConfig(Keys.tracker_currPageTag, tag);
        updateConfig(Keys.tracker_currPageModule, moduleName);
    }, [showAs, updateOnly, editPageTitle, addPageTitle]);

    return (<>
        {createViewAsPanel()}
        {canShowShareButton() && createSharePanel()}
    </>);

    function createViewAsPanel() {
        if (showAs == ShowViewType.DRAWER && isScreenXs) {
            // return createDialog();
        }
        switch (showAs) {
            case ShowViewType.PAGE:
                return createPage();
            case ShowViewType.PANEL:
                return createPanel();
            case ShowViewType.DIALOG:
                return createDialog();
            case ShowViewType.DRAWER:
                return createDrawer();
        }
    }

    function createDialog() {
        return <RootInternalDialog
            open={open}
            title={getTabTitle()}
            onHide={onHide}
            closeOnOutsideClick={false}>
            {createPanel()}
        </RootInternalDialog>
    }

    function createDrawer() {
        return <ThemedDrawer
            title={getTabTitle()}
            open={open}
            closeOnEscape={true}
            closeOnClickAway={false}
            onClose={onHide}
            width={drawerWidth}
            titleAt={'start'}
            titleClassName={'px-2 fs-lg'}
            headerContent={<div className={'me-4'}>
                {createHeaderControls()}
            </div>}>
            {createPanel()}
        </ThemedDrawer>
    }

    function createPage() {
        return <AppInterface
            contentWrapperProps={{className: 'mt-n1', bg: 'transparent', boxShadow: 'none'}}
            rootProps={{bg: 'transparent',}}
            showHome={true}>
            <GridBox className={'px-1'} sm={10} md={md} justifyAt={'center'}>
                <Section
                    className={`px-4 py-2 h-100 pb-3 app-section`}
                    title={getTabTitle()}
                    contentClassName={'px-2 py-1 position-relative'}
                    titleClassName={'fs-lg mb-2 d-flex jc-center'}
                    titleColor={theme.primary}
                    overflow={''}
                    borderless={true}
                    shadowVariant={theme.boxShadowMdSm}>
                    {createPanel()}
                </Section>
            </GridBox>
        </AppInterface>
    }

    function createPanel() {
        return <div className={'m-0 p-0 px-2 w-100 position-relative'}>
            <div>
                <div>
                    {freeComponent && freeComponent}
                </div>

                <InvalidPanel
                    wrapperClassName={''}
                    labelClassName={'m-2 w-100 fs-normal'}
                    color={theme.text70}
                    success={isBoolTrue(isDataFound)}
                    loading={isBoolTrue(isDataLoading)}
                    msg={dataLoadingMsg}
                    show404Image={true}
                />

                {/*header*/}
                {((!isBoolTrue(isDataLoading)) && isBoolTrue(isDataFound))
                    && <div className={'position-relativex'}>
                        {((isBoolTrue(showBack) && showAs == ShowViewType.PAGE
                                    && checkNullStr(getDefStr(backUrl, ModuleRouteUrls[moduleName])))
                                || canShowShareButton()) &&
                            <div className={'position-absolute'} style={{top: '-2.6rem', right: '.1rem'}}>
                                {showAs != ShowViewType.DRAWER && createHeaderControls()}
                            </div>}
                        {children}
                    </div>}
            </div>
        </div>
    }

    function createHeaderControls() {
        return <Flexbox alignAt={'center'}>
            {/*go-back btn*/}
            {(showAs == ShowViewType.PAGE && !isScreenUpSm) && <Anchor
                className={'fw-semi-bold'}
                textAt={'start'}
                iconClass={'bi bi-arrow-left text-primary'}
                href={getDefStr(backUrl, ModuleRouteUrls[moduleName])}
                onClick={() => {
                    updateConfig(Keys.config_showHomeRightPanels, true);
                }}>
                Go back
            </Anchor>}

            {/* share-button */}
            {(canShowShareButton()) && <ThemedIconButton
                className={'p-1 text-nowrap fs-sm fw-semi-bold'}
                m={1}
                ml={3}
                size={'22px'}
                outlined={true}
                variant={theme.primaryBlue}
                disableVariant={'xl'}
                icon={"fa fa-share"}
                tooltip={"Share"}
                circular={false}
                onClick={() => {
                    setShowShareDialog(true);
                }}
            />}
        </Flexbox>
    }

    function createSharePanel() {
        return <PageLinkSharePanel
            open={showShareDialog}
            customShareUrl={shareUrl}
            onHide={() => setShowShareDialog(false)}
            onSuccessAndHide={() => setShowShareDialog(false)}
        />
    }

    function getTabTitle() {
        // return isBoolTrue(updateOnly) ? getDefStr(editPageTitle, `Edit ${moduleName}`) : getDefStr(addPageTitle, `Create ${moduleName}`);
        return isBoolTrue(updateOnly) ? getDefStr(editPageTitle, `Edit ${moduleName}`) : getDefStr(addPageTitle, `Create ${moduleName}`);
    }

    function canShowShareButton() {
        return (isCurrUserRoleAdmin() && isBoolTrue(showShare) && showAs != ShowViewType.PAGE);
    }
});
export default FormPageRoot;
